import { useTheme } from "@emotion/react";
import { useContext, useState } from "react";

import { IamAuthContext } from "@megaron/auth-react";
import { useRegion } from "@megaron/region-context";

import { CubeCollapsedTileSkeletonStack } from "./CubeCollapsedTileSkeletonStack";
import { CubeHomeAccordionSection } from "./CubeHomeAccordionSection";
import { CustomerCubeFactTable } from "./CustomerCubeFactTable";
import {
  canQueryCrmCubeWithoutRegionSelect,
  hasCrmHomeCubeAccess,
  isCrmHomeCubeDirectorAnalytics,
} from "./cubeHomeEnv";
import { postCubeLoad } from "./fetchCubeLoad";
import { CustomerAvailabilityByRegionChart } from "./CustomerAvailabilityByRegionChart";
import { MyRegionAvailabilityBlock } from "./MyRegionAvailabilityBlock";
import { ProContractorsBlock } from "./ProContractorsBlock";
import { showWhenRowsOrError, useCrmHomeCubeQueries } from "./useCrmHomeCubeQueries";

const toError = (e: unknown): Error | null => {
  if (e instanceof Error) {
    return e;
  }
  if (e === undefined || e === null) {
    return null;
  }
  return new Error(String(e));
};

export const CrmHomeCubePanel = () => {
  const theme = useTheme();
  const auth = useContext(IamAuthContext);
  const roles = auth.iamUser?.roles ?? [];
  const { region, isLoaded: regionLoaded } = useRegion();
  const [isProChartOpen, setIsProChartOpen] = useState(false);
  const [isAdminAvailChartOpen, setIsAdminAvailChartOpen] = useState(false);
  const [isUnassignedOpen, setIsUnassignedOpen] = useState(false);
  const [isNoRegionPostalOpen, setIsNoRegionPostalOpen] = useState(false);
  const [isMyRegionAvailOpen, setIsMyRegionAvailOpen] = useState(false);

  const isDirectorAnalytics = isCrmHomeCubeDirectorAnalytics(roles);
  const regionTrimmed = region?.trim() ?? "";
  const canQueryCubeBase = auth.isLoaded && hasCrmHomeCubeAccess(roles);

  const q = useCrmHomeCubeQueries({
    cubeLoad: async (query) => postCubeLoad(query, await auth.getAuthHeader()),
    canQueryCubeBase,
    isDirectorAnalytics,
    regionTrimmed,
    regionLoaded,
    canQueryCubeWithoutRegionSelect: canQueryCrmCubeWithoutRegionSelect(roles),
  });

  if (!auth.isLoaded) {
    return null;
  }
  if (!hasCrmHomeCubeAccess(roles)) {
    return null;
  }

  const showPro = q.hasRegionScope && showWhenRowsOrError(q.proQuery);
  const showAdminAvail = isDirectorAnalytics && showWhenRowsOrError(q.adminAvailQuery);
  const showUnassigned = isDirectorAnalytics && showWhenRowsOrError(q.unassignedQuery);
  const showNoPostal = isDirectorAnalytics && showWhenRowsOrError(q.noRegionPostalQuery);
  const showMyAvail = !isDirectorAnalytics && showWhenRowsOrError(q.myAvailQuery);
  const hasVisibleSections =
    showPro || showAdminAvail || showUnassigned || showNoPostal || showMyAvail;

  if (!hasVisibleSections && !q.anyInitialLoading) {
    return null;
  }

  if (!hasVisibleSections && q.anyInitialLoading) {
    return <CubeCollapsedTileSkeletonStack />;
  }

  const proErr = toError(q.proQuery.error);
  const proLoading = q.proQuery.isFetching && q.proQuery.data === undefined;
  const adminAvailErr = toError(q.adminAvailQuery.error);
  const adminAvailLoading = q.adminAvailQuery.isFetching && q.adminAvailQuery.data === undefined;
  const unassignedErr = toError(q.unassignedQuery.error);
  const unassignedLoading = q.unassignedQuery.isFetching && q.unassignedQuery.data === undefined;
  const noPostalErr = toError(q.noRegionPostalQuery.error);
  const noPostalLoading = q.noRegionPostalQuery.isFetching && q.noRegionPostalQuery.data === undefined;
  const myAvailErr = toError(q.myAvailQuery.error);
  const myAvailLoading = q.myAvailQuery.isFetching && q.myAvailQuery.data === undefined;
  const myAvailRow = q.myAvailQuery.data?.[0];

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {showPro && (
        <CubeHomeAccordionSection
          title="Nowi wykonawcy PRO"
          isOpen={isProChartOpen}
          onToggle={() => {
            setIsProChartOpen((v) => !v);
          }}
        >
          {!isDirectorAnalytics && !regionLoaded && <CubeCollapsedTileSkeletonStack />}
          {!isDirectorAnalytics && regionLoaded && regionTrimmed.length === 0 && (
            <div css={{ color: theme.colors.secondaryText, fontSize: "0.875rem" }}>Brak przypisanego regionu.</div>
          )}
          {q.hasRegionScope && <ProContractorsBlock rows={q.proQuery.data} error={proErr} isLoading={proLoading} />}
        </CubeHomeAccordionSection>
      )}
      {showAdminAvail && (
        <CubeHomeAccordionSection
          title="Klienci z uzupełnioną dostępnością wg regionu"
          isOpen={isAdminAvailChartOpen}
          onToggle={() => {
            setIsAdminAvailChartOpen((v) => !v);
          }}
        >
          <CustomerAvailabilityByRegionChart
            rows={q.adminAvailQuery.data}
            error={adminAvailErr}
            isLoading={adminAvailLoading}
          />
        </CubeHomeAccordionSection>
      )}
      {showMyAvail && (
        <CubeHomeAccordionSection
          title="Dostępność produktów w Twoim regionie"
          isOpen={isMyRegionAvailOpen}
          onToggle={() => {
            setIsMyRegionAvailOpen((v) => !v);
          }}
        >
          {!regionLoaded && <CubeCollapsedTileSkeletonStack />}
          {regionLoaded && regionTrimmed.length === 0 && (
            <div css={{ color: theme.colors.secondaryText, fontSize: "0.875rem" }}>Brak przypisanego regionu.</div>
          )}
          {regionTrimmed.length > 0 && (
            <MyRegionAvailabilityBlock
              row={myAvailRow}
              error={myAvailErr}
              isLoading={myAvailLoading}
              regionLabel={regionTrimmed}
            />
          )}
        </CubeHomeAccordionSection>
      )}
      {showUnassigned && (
        <CubeHomeAccordionSection
          title="Nieprzypisani wykonawcy PRO"
          isOpen={isUnassignedOpen}
          onToggle={() => {
            setIsUnassignedOpen((v) => !v);
          }}
        >
          <CustomerCubeFactTable
            variant="unassignedPro"
            rows={q.unassignedQuery.data ?? []}
            error={unassignedErr}
            isLoading={unassignedLoading}
          />
        </CubeHomeAccordionSection>
      )}
      {showNoPostal && (
        <CubeHomeAccordionSection
          title="Klienci bez regionu z kodem pocztowym"
          isOpen={isNoRegionPostalOpen}
          onToggle={() => {
            setIsNoRegionPostalOpen((v) => !v);
          }}
        >
          <CustomerCubeFactTable
            variant="noRegionPostal"
            rows={q.noRegionPostalQuery.data ?? []}
            error={noPostalErr}
            isLoading={noPostalLoading}
          />
        </CubeHomeAccordionSection>
      )}
    </div>
  );
};
